import { useMemo } from 'react'

import { Cog, UserMinus, UserPlus } from 'lucide-react'

import { UserDisplay } from '~/components/component-library'
import { AddressDisplay } from '~/components/component-library'
import { useAccountUpdatedByMerkleTxn } from '~/components/txn-display/state-change/hooks/useAccountUpdatedByMerkleTxn'
import { MerkleStateChangeItem } from '~/components/txn-display/state-change/types'
import { useActiveOrg } from '~/hooks/org'
import {
  BackendTransaction,
  BasicTransactionType,
  MerkleTransactionCallType,
  OrgMerkleTransaction,
} from '~/types'
import { appendEmailToAuthenticatorName } from '~/utils/smartAccounts'

export const useSignerUpdates = ({
  transaction,
}: {
  transaction: BackendTransaction
}): MerkleStateChangeItem[] => {
  const account = useAccountUpdatedByMerkleTxn(transaction)
  const { org: activeOrg, isLoading } = useActiveOrg()

  return useMemo(() => {
    if (!transaction.types.includes(BasicTransactionType.UpdateSigners))
      return []

    const contents: MerkleStateChangeItem[] = []

    ;(transaction as OrgMerkleTransaction).txData.transactions.forEach((tx) => {
      switch (tx.type) {
        case MerkleTransactionCallType.AddSigner: {
          const user = activeOrg?.members.find(
            ({ email }) => email === tx.email,
          )
          if (!account) {
            contents.push({
              title: 'Add signer',
              icon: <UserPlus />,
            })
          } else {
            contents.push({
              title: (
                <span>
                  Add signer to{' '}
                  <AddressDisplay
                    address={account.address}
                    layout="inline-text"
                  />
                </span>
              ),
              icon: <UserPlus />,
              description: user ? (
                <span className="flex items-center gap-1">
                  <span>
                    <UserDisplay user={user} layout="standard" size="small" />
                  </span>
                  ({tx.name})
                </span>
              ) : (
                appendEmailToAuthenticatorName(tx.name, tx.email)
              ),
              isLoading,
            })
          }
          break
        }
        case MerkleTransactionCallType.RemoveSigner: {
          const user = activeOrg?.members.find(
            ({ email }) => email === tx.email,
          )
          if (!account) {
            contents.push({
              title: 'Remove signer',
              icon: <UserMinus />,
            })
          } else {
            contents.push({
              title: (
                <span>
                  Remove signer from{' '}
                  <AddressDisplay
                    address={account.address}
                    layout="inline-text"
                  />
                </span>
              ),
              icon: <UserMinus />,
              description: user ? (
                <span className="flex items-center gap-1">
                  <span>
                    <UserDisplay user={user} layout="standard" size="small" />
                  </span>
                  ({tx.name})
                </span>
              ) : (
                appendEmailToAuthenticatorName(tx.name, tx.email)
              ),
              isLoading,
            })
          }
          break
        }
        case MerkleTransactionCallType.UpdateThreshold:
          if (!account) {
            contents.push({
              title: 'Update threshold',
              icon: <Cog />,
            })
          } else {
            contents.push({
              title: (
                <span>
                  Update threshold on{' '}
                  <AddressDisplay
                    address={account.address}
                    layout="inline-text"
                  />
                </span>
              ),
              icon: <Cog />,
              description: `${
                tx.oldThreshold > tx.newThreshold ? 'Decrease' : 'Increase'
              } from ${tx.oldThreshold} to ${tx.newThreshold}`,
              isLoading,
            })
          }
          break
        default:
          contents.push({
            title: tx.type,
            icon: <Cog />,
            description: JSON.stringify(tx),
            isLoading,
          })
          break
      }
    })

    return contents
  }, [transaction, account, activeOrg?.members, isLoading])
}
